// ============================================================
// SALT - Modal Component
// Matches existing SL design with ReactDOM portal
// ============================================================

import React, { useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useOverlayFocus } from '../../hooks/useOverlayFocus';

interface ModalProps {
  isOpen: boolean;
  title?: string;
  message?: string;
  children?: React.ReactNode;

  confirmLabel?: string;
  cancelLabel?: string;

  onConfirm?: () => void;
  onCancel?: () => void;
  onClose?: () => void;

  disableConfirm?: boolean;
  disableCancel?: boolean;

  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
}

export default function Modal({
  isOpen,
  title,
  message,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  onClose,
  disableConfirm = false,
  disableCancel = false,
  closeOnBackdrop = true,
  closeOnEsc = true,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    if (typeof onClose === 'function') return onClose();
    if (typeof onCancel === 'function') return onCancel();
  }, [onClose, onCancel]);

  useOverlayFocus(modalRef, isOpen);

  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closeOnEsc, handleClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="sl-modal-backdrop"
      onClick={() => {
        if (closeOnBackdrop) handleClose();
      }}
    >
      <div
        ref={modalRef}
        className="sl-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Modal'}
        tabIndex={-1}
      >
        {title && <h3 className="sl-modal-title">{title}</h3>}

        {message && <p className="sl-modal-text">{message}</p>}
        {!message && children ? <div className="sl-modal-body">{children}</div> : null}

        <div className="sl-modal-actions">
          <button
            type="button"
            className="sl-modal-button sl-modal-button-secondary"
            onClick={handleClose}
            disabled={disableCancel}
          >
            {cancelLabel}
          </button>

          <button
            type="button"
            className="sl-modal-button sl-modal-button-primary"
            onClick={() => typeof onConfirm === 'function' && onConfirm()}
            disabled={disableConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
